import React, { useState, useEffect, useCallback, useRef } from "react";
import toast from "react-hot-toast";
import {
  Box,
  Tooltip,
  MenuItem,
  Stack,
  Select,
  FormControl,
  InputLabel,
} from "@mui/material";
import { Refresh, DeleteOutline } from "@mui/icons-material";
import { LAYOUT_HEIGHT } from "state/constants";
import api from "state/axios";
import Header from "components/Header";
import FlexBetween from "components/FlexBetween";
import StyledButton from "components/StyledButton";
import ScrollToTop from "components/ScrollToTop";
import RequestForm from "./RequestForm";
import RequestList from "./RequestList";
import RequestCards from "./RequestCards";
import { REQUEST_TYPE_OPTIONS, STATUS_OPTIONS } from "./constants";


export default function DataRequests() {    
  const scrollRef = useRef(null);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [rowSelectionModel, setRowSelectionModel] = useState({ type: "include", ids: new Set() });
  const [filterStatus, setFilterStatus] = useState("");
  const [filterType, setFilterType] = useState("");
  const [columnVisibilityModel, setColumnVisibilityModel] = useState({});

  const selectedIds = Array.from(rowSelectionModel.ids || []);

  const fetchRequests = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 200 };
      if (filterStatus) params.status = filterStatus;
      if (filterType) params.requestTypes = filterType;

      const { data } = await api.get("/datarequests", { params });
      setRows(data.data || []);
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to load requests!");
    } finally {
      setLoading(false);
    }
  }, [filterStatus, filterType]);

  useEffect(() => { fetchRequests(); }, [fetchRequests]);

  const handleDeleteMany = async (ids) => {
    try {
      await api.delete("/datarequests/delete-many", { data: { ids } });
      toast.success(`${ids.length} ${ids.length === 1 ? "item" : "items"} deleted.`);
      setRowSelectionModel({ type: "include", ids: new Set() });
      fetchRequests();
    } catch (err) {
      toast.error(err.response?.data?.message || "Delete failed!");
    }
  };

  return (
    <Box className="main-container-box">
      <FlexBetween>
        <Header 
          title="Data Requests" 
          subtitle="List of Requests" 
        />
        <Stack 
          direction="row" 
          alignItems="center"
          spacing={1} 
        >
          <FormControl
            size="small"
            sx={{
              width: { xs: '6rem', sm: '8rem' },
            }}
          >
            <InputLabel>Status</InputLabel>
            <Select 
              label="Status" 
              value={filterStatus} 
              onChange={(e) => setFilterStatus(e.target.value)}
            >
              <MenuItem value="">All</MenuItem>
              {STATUS_OPTIONS?.map((s) => (
                <MenuItem key={s} value={s}>{s}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl
            size="small"
            sx={{
              width: { xs: '6rem', sm: '12rem' },
            }}
          >
            <InputLabel>Type</InputLabel>
            <Select 
              label="Type" 
              value={filterType} 
              onChange={(e) => setFilterType(e.target.value)}
            >
              <MenuItem value="">All</MenuItem>
              {REQUEST_TYPE_OPTIONS?.map(({ value, label }) => (
                <MenuItem key={value} value={value}>{label}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <Tooltip title="Refresh">
            <span>
              <StyledButton 
                disabled={loading}
                onClick={fetchRequests} 
              >
                <Refresh />
              </StyledButton>
            </span>
          </Tooltip>

          {selectedIds.length > 0 && (
            <Tooltip title={`Delete ${selectedIds.length} selected`}>
              <span>
                <StyledButton onClick={() => handleDeleteMany(selectedIds)}>
                  <DeleteOutline />
                </StyledButton>
              </span>
            </Tooltip>
          )}
        </Stack>
      </FlexBetween>

      <Box
        ref={scrollRef}
        height={LAYOUT_HEIGHT}
        sx={{ overflowY: "auto" }}
      >
        <Box
          display="grid"
          gridTemplateColumns="repeat(12, minmax(0, 1fr))"
          gap={2}
        >
          <Box gridColumn={{ xs: "span 12", md: "span 3" }}>          
            <RequestForm onCreated={fetchRequests} />
          </Box>

          <Box gridColumn={{ xs: "span 12", md: "span 9" }}>
            <Box sx={{ display: { xs: "block", sm: "none" } }}>
              <RequestCards
                rows={rows}
                loading={loading}
                onDelete={handleDeleteMany}
                onStatusUpdated={fetchRequests}
              />
            </Box>

            <Box sx={{ display: { xs: "none", sm: "block" } }}>
              <RequestList
                rows={rows}
                loading={loading}
                rowSelectionModel={rowSelectionModel}
                setRowSelectionModel={setRowSelectionModel}
                columnVisibilityModel={columnVisibilityModel}
                setColumnVisibilityModel={setColumnVisibilityModel}
                onDelete={handleDeleteMany}
                onStatusUpdated={fetchRequests}
              />
            </Box>
          </Box>
        </Box>

        <ScrollToTop scrollRef={scrollRef} />
      </Box>
    </Box>
  );
}
